import { BadgeDollarSign, BadgePercent, Gift } from 'lucide-react'
import { formatPrice } from '@/utils/util'

export type CampaignDiscountInfo = {
  icon: any
  label: string
  suffix: string
  color: string
}

/*
  One label for every target-role value of a campaign: a single percent "20%", several percents "20% хүртэл"
  (the largest), amounts only the largest amount, all free "Үнэгүй". A free target among others counts as 100%,
  the strongest value. Percents and amounts mixed: the percent label with "хүртэл".
*/
export function getCampaignDiscountInfo(targets: any[]): CampaignDiscountInfo | null {
  if (!targets || targets.length === 0) return null
  const valued = targets.filter((t: any) => (t.role ?? 'target') === 'target')
  const percents: number[] = []
  const amounts: number[] = []
  let freeCount = 0
  for (const t of valued) {
    if (t.valueType === 'free' || (t.valueType === 'percent' && t.valuePercent >= 100)) freeCount++
    else if (t.valueType === 'percent' && t.valuePercent > 0) percents.push(t.valuePercent)
    else if (t.valueType === 'amount' && t.valueAmount > 0) amounts.push(t.valueAmount)
  }
  const valueCount = freeCount + percents.length + amounts.length
  if (valueCount === 0) return null
  if (freeCount === valueCount) {
    return { icon: Gift, label: 'Үнэгүй', suffix: '', color: 'bg-green-100 text-green-600' }
  }
  if (freeCount > 0 || percents.length > 0) {
    const maxPercent = freeCount > 0 ? 100 : Math.max(...percents)
    const single = freeCount === 0 && amounts.length === 0 && new Set(percents).size === 1
    if (single) return { icon: BadgePercent, label: `${maxPercent}%`, suffix: 'хувиар хямдарна', color: 'bg-red-100 text-red-600' }
    return { icon: BadgePercent, label: `${maxPercent}% хүртэл`, suffix: 'хямдарна', color: 'bg-red-100 text-red-600' }
  }
  const maxAmount = Math.max(...amounts)
  const label = String(formatPrice(maxAmount) || '')
  if (new Set(amounts).size === 1) return { icon: BadgeDollarSign, label, suffix: 'төгрөгөөр хямдарна', color: 'bg-blue-100 text-blue-600' }
  return { icon: BadgeDollarSign, label: `${label} хүртэл`, suffix: 'хямдарна', color: 'bg-blue-100 text-blue-600' }
}

export type CampaignTargetInfo =
  | { type: 'all' }
  | { type: 'category', categoryIds: string[] }
  | { type: 'product', productIds: string[] }
  | { type: 'none' }

export function getCampaignTargetInfo(targets: any[]): CampaignTargetInfo {
  if (!targets || targets.length === 0) return { type: 'none' }
  const allProductsTarget = targets.find((t: any) => (t.role === 'target' || t.role === 'trigger') && t.targetId === null)
  if (allProductsTarget) return { type: 'all' }
  const categoryTargets = targets.filter((t: any) => t.targetType === 'category' && (t.role === 'target' || t.role === 'trigger') && t.targetId)
  if (categoryTargets.length > 0) return { type: 'category', categoryIds: categoryTargets.map((t: any) => t.targetId) }
  const productTargets = targets.filter((t: any) => t.targetType === 'product' && t.targetId)
  if (productTargets.length > 0) return { type: 'product', productIds: productTargets.map((t: any) => t.targetId) }
  return { type: 'none' }
}

export type CampaignStatus = 'upcoming' | 'active' | 'expired'

export function getCampaignStatus(startDate: string | null | undefined, endDate: string | null | undefined, now: Date = new Date()): CampaignStatus {
  const active = startDate ? new Date(startDate) <= now : true
  const expired = endDate ? new Date(endDate) < now : false
  if (expired) return 'expired'
  if (!active) return 'upcoming'
  return 'active'
}

export type CountdownParts = { d: number, h: number, m: number, s: number }

export function getCountdownParts(endDate: string, now: Date = new Date()): CountdownParts | null {
  const diff = new Date(endDate).getTime() - now.getTime()
  if (diff <= 0) return null
  return {
    d: Math.floor(diff / (1000 * 60 * 60 * 24)),
    h: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    m: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    s: Math.floor((diff % (1000 * 60)) / 1000),
  }
}
